import { useEffect, useMemo, useRef, useState, useCallback, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import BlogHero from './components/BlogHero';
import BlogCard from './components/BlogCard';
import BlogCardSkeleton from './components/BlogCardSkeleton';
import BrowseByTopic from './components/BrowseByTopic';
import { useBlogPosts } from '@/hooks/useBlogPosts';
import { SITE_URL } from '@/components/base/Seo';

export default function BlogListing() {
  const { publishedPosts, resetToDefaults } = useBlogPosts();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');
  const [isFiltering, setIsFiltering] = useState(false);
  const isFirstRender = useRef(true);

  // Brief shimmer pass on every category switch so instant filtering feels intentional
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setIsFiltering(true);
    const timer = window.setTimeout(() => setIsFiltering(false), 420);
    return () => window.clearTimeout(timer);
  }, [activeCategory]);

  const filteredPosts = useMemo(() => {
    if (activeCategory === 'All') return publishedPosts;
    const target = activeCategory.toLowerCase();
    return publishedPosts.filter((post) => (post.category || '').trim().toLowerCase() === target);
  }, [publishedPosts, activeCategory]);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const openPassword = useCallback(() => {
    setPasswordValue('');
    setPasswordError('');
    setPasswordOpen(true);
  }, []);

  const closePassword = useCallback(() => {
    setPasswordOpen(false);
    setPasswordValue('');
    setPasswordError('');
  }, []);

  const submitPassword = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (passwordValue === 'ESM2026') {
        try {
          localStorage.setItem('blogAdminAuthed', '1');
        } catch {
          // localStorage not available
        }
        navigate('/blog/admin');
      } else {
        setPasswordError('Incorrect password. Please try again.');
      }
    },
    [passwordValue, navigate],
  );

  useEffect(() => {
    document.title = 'Blog | Federica Tedeschi — Energy-Safe Coaching Insights for Chronic Fatigue & Pain';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Read gentle insights on nervous system regulation, energy-safe coaching, chronic fatigue, chronic pain, NLP techniques, and self-compassion. Blog by Federica Tedeschi, Energy-Safe Transformational Coach.');
    }
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'energy-safe coaching blog,chronic fatigue insights,chronic pain blog,nervous system regulation,NLP techniques,self-compassion healing');
    }

    const setProperty = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const setName = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const setCanonical = (href: string) => {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = href;
    };

    const listingUrl = `${SITE_URL}/blog`;
    const ogImage = 'https://storage.readdy-site.link/project_files/185c4543-d0dc-49d0-8dbb-59fc7deb7f59/4d5c3037-c9af-47f6-bdf8-dc98d98f2d0c_Banner-2-.png?v=9601c2c8a8e1bc8c8627c0876333b127';

    setCanonical(listingUrl);
    setProperty('og:url', listingUrl);
    setProperty('og:image', ogImage);
    setName('twitter:card', 'summary_large_image');
    setName('twitter:title', 'Blog | Federica Tedeschi');
    setName('twitter:description', 'Read gentle insights on nervous system regulation, energy-safe coaching, chronic fatigue, chronic pain, NLP techniques, and self-compassion.');
    setName('twitter:image', ogImage);

    return () => {
      document.title = 'Federica Tedeschi | Energy-Safe Transformational Coach for Chronic Fatigue & Pain';
      if (metaDesc) {
        metaDesc.setAttribute('content', 'Energy-Safe Transformational Coach for Chronic Fatigue & Pain. CCE-ICF Accredited. NLP-informed coaching, nervous system regulation, and personalised strategies to help you reclaim your life.');
      }
      if (metaKeywords) {
        metaKeywords.setAttribute('content', 'energy-safe coaching,chronic fatigue coach,chronic pain coach,NLP coaching,nervous system regulation');
      }
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
      document.querySelectorAll('meta[property="og:url"], meta[property="og:image"], meta[name="twitter:card"], meta[name="twitter:title"], meta[name="twitter:description"], meta[name="twitter:image"]').forEach((el) => el.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <BlogHero />

        {/* Browse by Category & Topic */}
        <BrowseByTopic
          posts={publishedPosts}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
        />

        {/* Structured Data for Blog Listing */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Blog',
              name: 'The Healing & Breakthrough Blog',
              description: 'Gentle insights on nervous system regulation, energy-safe coaching, chronic illness, and the art of healing at your own pace.',
              url: `${SITE_URL}/blog`,
              author: {
                '@type': 'Person',
                name: 'Federica Tedeschi',
              },
              blogPost: publishedPosts.map((post) => ({
                '@type': 'BlogPosting',
                headline: post.title,
                description: post.excerpt || '',
                url: `${SITE_URL}/blog/${post.slug}`,
                datePublished: post.publishedAt,
                author: {
                  '@type': 'Person',
                  name: post.author,
                },
              })),
            }),
          }}
        />

        {/* Blog Posts Grid */}
        <section className="section-padding max-w-6xl mx-auto">
          {publishedPosts.length === 0 ? (
            <div className="text-center py-20">
              <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-article-line text-4xl text-foreground-300" />
              </span>
              <h2 className="font-heading text-2xl font-semibold text-foreground-800 mb-2">
                No posts yet
              </h2>
              <p className="text-sm text-foreground-500 mb-4">
                Check back soon for new articles on healing, nervous system regulation, and energy-safe coaching.
              </p>
              <button
                onClick={resetToDefaults}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
                type="button"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-refresh-line text-sm" />
                </span>
                Restore Sample Posts
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900">
                    {activeCategory === 'All' ? 'Latest Articles' : activeCategory}
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">
                    {activeCategory === 'All'
                      ? 'Gentle insights for your healing journey'
                      : `${filteredPosts.length} ${filteredPosts.length === 1 ? 'article' : 'articles'} in this category`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={openPassword}
                  className="text-xs font-medium text-foreground-400 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-settings-3-line text-xs" />
                  </span>
                  Manage
                </button>
              </div>

              {isFiltering ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Array.from({ length: filteredPosts.length || 6 }).map((_, i) => (
                    <BlogCardSkeleton key={i} />
                  ))}
                </div>
              ) : filteredPosts.length === 0 ? (
                <div className="text-center py-16">
                  <span className="w-14 h-14 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-inbox-line text-3xl text-foreground-300" />
                  </span>
                  <p className="text-sm text-foreground-500 mb-4">
                    No articles in this category yet.
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveCategory('All')}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-refresh-line text-sm" />
                    </span>
                    Show all articles
                  </button>
                </div>
              ) : (
                <div
                  key={activeCategory}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 grid-fade-in"
                >
                  {filteredPosts.map((post) => (
                    <BlogCard key={post.id} post={post} />
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </main>
      <Footer />

      {passwordOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
          <div className="bg-background-50 rounded-lg max-w-sm w-full p-6">
            <div className="flex items-start justify-between mb-1">
              <h2 className="font-heading text-lg font-semibold text-foreground-900">
                Enter Password
              </h2>
              <button
                type="button"
                onClick={closePassword}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="Close password dialog"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-close-line text-base" />
                </span>
              </button>
            </div>
            <p className="text-sm text-foreground-500 mb-4">
              Enter the admin password to manage blog posts.
            </p>
            <form onSubmit={submitPassword} className="space-y-3">
              <div>
                <label
                  htmlFor="blog-admin-password"
                  className="block text-xs font-medium text-foreground-600 mb-1.5"
                >
                  Password
                </label>
                <input
                  id="blog-admin-password"
                  name="blog_admin_password"
                  type="password"
                  value={passwordValue}
                  onChange={(e) => setPasswordValue(e.target.value)}
                  autoFocus
                  className="w-full px-3 py-2 text-sm text-foreground-900 bg-background-50 border border-background-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 placeholder:text-foreground-300"
                  placeholder="Enter password"
                />
                {passwordError && (
                  <p className="text-xs text-red-500 mt-1.5">{passwordError}</p>
                )}
              </div>
              <div className="flex items-center justify-end gap-3 pt-1">
                <button
                  type="button"
                  onClick={closePassword}
                  className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Unlock
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}